import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Carousel from "react-bootstrap/Carousel";
import { POSTERS, downloadPoster } from "./SchoolAdmin/Swachhata/swachhataContent";
import videoPoster from "../assets/images/swachhata/swm-rules-2026-poster.jpg";
import { SWACHHATA_QUIZ_OPEN } from "../config/campaigns";
import "./SwachhataHome.css";

/*
 * Swachhata Hi Seva 2026 on the landing page:
 *   swachhataPosterSlides() — the six Ministry SWM posters as hero-carousel
 *     slides (returned as an array so react-bootstrap sees Carousel.Items).
 *     The poster is the hero of each slide; copy and buttons stay small.
 *   <SwachhataVideoSection> — the SWM Rules 2026 video in a tall player (it is
 *     a 9:16 vertical video) next to the quiz (while open) / E-Waste events.
 *
 * The video is a 720×1280 web encode of the Ministry's 591 MB Drive original
 * (Drive's own player failed on it), served from public/swachhata/.
 */

export const SWM_VIDEO_SRC = `${process.env.PUBLIC_URL || ""}/swachhata/swm-rules-2026.mp4`;
export const SWM_EVENTS_PATH = "/swachhata-hi-seva/events";
export const SWM_LEARN_PATH = "/swachhata-hi-seva?start=learn";
export const SWM_QUIZ_PATH = "/swachhata-hi-seva?start=quiz";
export const EWASTE_EVENTS_PATH = "/special-campaign-6/e-waste-events"; // E-Waste belongs to Special Campaign 6

export function swachhataPosterSlides() {
  return POSTERS.map((p, i) => (
    <Carousel.Item key={`swm-${p.key}`}>
      <div className="swm-slide">
        <span className="swm-slide__blur" style={{ backgroundImage: `url(${p.src})` }} aria-hidden="true" />
        <div className="swm-slide__copy">
          <span className="swm-slide__kicker">
            <i aria-hidden="true" />
            Swachhata Hi Seva 2026
          </span>
          <h3 className="swm-slide__title">{p.title}</h3>
          <span className="swm-slide__count">{`Poster ${i + 1} of ${POSTERS.length}`}</span>
          <div className="swm-slide__cta">
            <Link to={SWM_LEARN_PATH} className="swm-slide__btn">
              Learning material
            </Link>
            {SWACHHATA_QUIZ_OPEN && (
              <Link to={SWM_QUIZ_PATH} className="swm-slide__btn swm-slide__btn--ghost">
                Take the quiz
              </Link>
            )}
            <button
              type="button"
              className="swm-slide__btn swm-slide__btn--ghost"
              onClick={() => downloadPoster(p)}
              aria-label={`Download the poster: ${p.title}`}
            >
              Download poster
            </button>
          </div>
        </div>
        <div className="swm-slide__poster">
          <img src={p.src} alt={p.title} />
        </div>
        <ul className="swm-slide__thumbs" aria-hidden="true">
          {POSTERS.map((t, k) => (
            <li key={t.key} className={k === i ? "is-on" : ""} style={{ "--d": `${k * 0.05}s` }}>
              <img src={t.src} alt="" />
            </li>
          ))}
        </ul>
      </div>
    </Carousel.Item>
  ));
}

const ICONS = {
  quiz: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12.5 3 3 5-6" />
    </svg>
  ),
  ewaste: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
      <path d="m10 8.5 2-1.5 2 1.5M14 12l-2 1.5-2-1.5" />
    </svg>
  ),
};

// Learning material and SWM events were taken off this list on 2026-10-22
// (the routes still exist; the poster slides keep their learning link).
const ACTIONS = [
  // dropped while the quiz is closed (config/campaigns.js)
  ...(SWACHHATA_QUIZ_OPEN ? [{ key: "quiz", to: SWM_QUIZ_PATH, title: "Take the quiz", text: "10 questions and a certificate", tone: "orange" }] : []),
  { key: "ewaste", to: EWASTE_EVENTS_PATH, title: "E-Waste collection in schools", text: "Items collected and photographs from schools", tone: "teal" },
];

export function SwachhataVideoSection() {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    setStarted(true);
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  return (
    <section className="swm-video" aria-labelledby="swm-video-title">
      <span className="swm-video__leaf swm-video__leaf--1" aria-hidden="true" />
      <span className="swm-video__leaf swm-video__leaf--2" aria-hidden="true" />
      <div className="swm-video__inner">
        <div className="swm-video__stage">
          <span className="swm-video__halo" aria-hidden="true" />
          <div className="swm-video__frame">
            <video
              ref={videoRef}
              className="swm-video__el"
              src={SWM_VIDEO_SRC}
              poster={videoPoster}
              controls={started}
              playsInline
              preload="metadata"
              onPlay={() => setStarted(true)}
            />
            {!started && (
              <button type="button" className="swm-video__play" onClick={play} aria-label="Play the Solid Waste Management Rules 2026 video">
                <span className="swm-video__play-btn" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M8 5.5v13l11-6.5Z" />
                  </svg>
                </span>
                <span className="swm-video__play-label">Watch the video</span>
              </button>
            )}
          </div>
        </div>

        <div className="swm-video__content">
          <span className="swm-video__kicker">
            <i aria-hidden="true" />
            {"Swachhata Hi Seva 2026 · Swachh Paathshala: Yuva for Swachhata"}
          </span>
          <h2 id="swm-video-title" className="swm-video__title">
            Solid Waste Management Rules, 2026
          </h2>
          <p className="swm-video__lead">
            {SWACHHATA_QUIZ_OPEN
              ? "Watch the video and take the quiz. Then see how schools across India are taking part."
              : "Watch the video. Then see how schools across India collected E-Waste."}
          </p>

          <div className="swm-actions">
            {ACTIONS.map((a, k) => (
              <Link key={a.key} to={a.to} className={`swm-action swm-action--${a.tone}`} style={{ "--d": `${k * 0.08}s` }}>
                <span className="swm-action__icon" aria-hidden="true">
                  {ICONS[a.key]}
                </span>
                <span className="swm-action__text">
                  <b>{a.title}</b>
                  <small>{a.text}</small>
                </span>
                <span className="swm-action__arrow" aria-hidden="true">
                  →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
